// 导航菜单数据 + 类型定义
// 由 Header.vue 导入使用
// - 两级：dropdownList[].list[] 直接是条目
// - 三级：dropdownList[].list[] 是分类（带 list），分类下再挂条目（如 projects 的「开源核心库」）

export interface DropdownItem {
    name: string;
    path?: string; // 路由路径 / 外部链接 URL
    desc?: string; // 子标题描述（显示在名称下方）
    enPath?: string; // 英文状态下的跳转地址（缺省时用 path）
    type?: 'route' | 'link' | 'unopen'; // route=内部路由，link=外部链接，unopen=弹窗提示未开放
    list?: DropdownItem[]; // 存在此字段表示三层菜单的分类节点
    badge?: string; // 名称右侧角标文案（如 HOT / NEW）
    isEnSeparateLink?: boolean; // 英文状态是否为单独的链接
    layout?: 'cards' | 'tiles' | 'list'; // 分组排版：cards=分类卡片（软件栈左栏），tiles=磁贴网格（工具&平台左栏），list=普通条目列表
    variant?: 'subgroup' | 'foundation' | 'guide'; // cards 排版下的卡片类型：subgroup=算子库分组卡，foundation=基础组件卡（编译器/框架/通信库）；guide=磁贴内的适配路径分组（开发者中心·芯片适配指南）
    wide?: boolean; // cards 排版下：该分组卡在两列栅格中独占一行（如「多领域算子库」）
    columns?: number; // cards 排版下卡片内条目列数（1 或 2）
}

export interface MenuItem {
    name: string;
    path?: string;
    dropdownList?: DropdownItem[];
    dropdownLayout?: 'list' | 'grid'; // 单层下拉的排版方式：list=垂直列表，grid=flex 网格
    active?: boolean; // 当前是否高亮（运行时由 Header.vue 维护）
}

export const menuList: MenuItem[] = [
    // 软件栈（projects）：三栏下拉
    // 左栏「开源核心库」为卡片分组（分类卡片 → 条目），中/右栏为普通条目列表
    {
        name: 'common.projects',
        dropdownList: [
            {
                name: 'tools.openSourceCoreLibrary',
                layout: 'cards',
                list: [
                    // 1. 通用算子库
                    {
                        name: 'tools.commonOperatorLibrary',
                        list: [
                            {
                                name: 'tools.menuLeft.FlagGems',
                                path: 'https://docs.flagos.io/projects/FlagGems/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagGems/en/latest/',
                                desc: 'tools.menuLeft.FlagGemsNavDesc',
                                type: 'link',
                            },
                        ],
                    },
                    // 2. 融合算子库
                    {
                        name: 'tools.fusedOperatorLibrary',
                        list: [
                            {
                                name: 'tools.menuLeft.FlagGemsVllm',
                                path: 'https://docs.flagos.io/projects/FlagGems-vllm/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagGems-vllm/en/latest/',
                                desc: 'tools.menuLeft.FlagGemsVllmNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagGemsSGLang',
                                path: 'https://docs.flagos.io/projects/FlagGems-sglang/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagGems-sglang/en/latest/',
                                desc: 'tools.menuLeft.FlagGemsSGLangNavDesc',
                                type: 'link',
                                badge: 'NEW',
                            },
                        ],
                    },
                    // 3. 多领域算子库（卡片内两列）
                    {
                        name: 'tools.multiDomainOperatorLibrary',
                        wide: true,
                        columns: 2,
                        list: [
                            {
                                name: 'tools.menuLeft.FlagDNN',
                                path: 'https://docs.flagos.io/projects/FlagDNN/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagDNN/en/latest/',
                                desc: 'tools.menuLeft.FlagDNNNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagBLAS',
                                path: 'https://docs.flagos.io/projects/FlagBLAS/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagBLAS/en/latest/',
                                desc: 'tools.menuLeft.FlagBLASNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagFFT',
                                path: 'https://docs.flagos.io/projects/FlagFFT/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagFFT/en/latest/',
                                desc: 'tools.menuLeft.FlagFFTNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagSparse',
                                path: 'https://docs.flagos.io/projects/FlagSparse/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagSparse/en/latest/',
                                desc: 'tools.menuLeft.FlagSparseNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagTensor',
                                path: 'https://docs.flagos.io/projects/FlagTensor/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagTensor/en/latest/',
                                desc: 'tools.menuLeft.FlagTensorNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagAttention',
                                path: 'https://docs.flagos.io/projects/FlagAttention/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagAttention/en/latest/',
                                desc: 'tools.menuLeft.FlagAttentionNavDesc',
                                type: 'link',
                            },
                        ],
                    },
                    // 4. 编译器
                    {
                        name: 'tools.compiler',
                        variant: 'foundation',
                        list: [
                            {
                                name: 'tools.menuLeft.FlagTree',
                                path: 'https://docs.flagos.io/projects/FlagTree/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagTree/en/latest/',
                                desc: 'tools.menuLeft.FlagTreeNavDesc',
                                type: 'link',
                            },
                            {
                                name: 'tools.menuLeft.FlagTreeCPU',
                                path: 'https://docs.flagos.io/projects/FlagTree/zh-cn/latest/getting_started/install-arm64-cpu.html',
                                enPath: 'https://docs.flagos.io/projects/FlagTree/en/latest/getting_started/install-arm64-cpu.html',
                                desc: 'tools.menuLeft.FlagTreeCPUNavDesc',
                                type: 'link',
                            },
                        ],
                    },
                    // 5. 训练与推理框架
                    {
                        name: 'tools.trainingAndInferenceFramework',
                        variant: 'foundation',
                        list: [
                            {
                                name: 'tools.menuLeft.FlagScale',
                                path: 'https://docs.flagos.io/projects/FlagScale/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagScale/en/latest/',
                                desc: 'tools.menuLeft.FlagScaleNavDesc',
                                type: 'link',
                            },
                        ],
                    },
                    // 6. 通信库
                    {
                        name: 'tools.communicationLibrary',
                        variant: 'foundation',
                        list: [
                            {
                                name: 'tools.menuLeft.FlagCX',
                                path: 'https://docs.flagos.io/projects/FlagCX/zh-cn/latest/',
                                enPath: 'https://docs.flagos.io/projects/FlagCX/en/latest/',
                                desc: 'tools.menuLeft.FlagCXNavDesc',
                                type: 'link',
                            },
                        ],
                    },
                ],
            },
            {
                name: 'tools.ecosystemEnablementProject',
                list: [
                    {
                        name: 'tools.menuCenter.MegatronLMFL',
                        path: 'https://docs.flagos.io/projects/Megatron-LM-FL/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/Megatron-LM-FL/en/latest/',
                        desc: 'tools.menuCenter.MegatronLMFLDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuCenter.vLLMPluginFL',
                        path: 'https://docs.flagos.io/projects/vllm-plugin-FL/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/vllm-plugin-FL/en/latest/',
                        desc: 'tools.menuCenter.vLLMPluginFLDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuCenter.TransformerEngineFL',
                        path: 'https://docs.flagos.io/projects/TransformerEngine-FL/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/TransformerEngine-FL/en/latest/',
                        desc: 'tools.menuCenter.TransformerEngineFLDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuCenter.VeRLFL',
                        path: 'https://docs.flagos.io/projects/verl-FL/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/verl-FL/en/latest/',
                        desc: 'tools.menuCenter.VeRLFLDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuCenter.SGLangPluginFL',
                        path: 'https://docs.flagos.io/projects/sglang-plugin-FL/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/sglang-plugin-FL/en/latest/',
                        desc: 'tools.menuCenter.SGLangPluginFLDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuCenter.PyTorchPluginFL',
                        path: 'https://docs.flagos.io/projects/torch-FL/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/torch-FL/en/latest/',
                        desc: 'tools.menuCenter.PyTorchPluginFLDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuCenter.VerlHardwarePlugin',
                        path: 'https://docs.flagos.io/projects/verl-hardware-plugin/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/verl-hardware-plugin/en/latest/',
                        desc: 'tools.menuCenter.VerlHardwarePluginDesc',
                        type: 'link',
                        badge: 'NEW',
                    },
                ],
            },
            {
                name: 'tools.domainSpecialProject',
                list: [
                    {
                        name: 'tools.menuRight.FlagOSRobo',
                        path: 'https://docs.flagos.io/projects/FlagOS-Robo/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/FlagOS-Robo/en/latest/',
                        desc: 'tools.menuRight.FlagOSRoboDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuRight.FlagQuantum',
                        path: 'https://docs.flagos.io/projects/FlagQuantum/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/FlagQuantum/en/latest/',
                        desc: 'tools.menuRight.FlagQuantumDesc',
                        type: 'link',
                    },
                ],
            },
        ],
    },
    // 工具&平台（tools）：两栏下拉 —— 开发者工具（左栏磁贴网格）/ 平台服务（右栏竖向列表）
    {
        name: 'common.tools',
        dropdownList: [
            {
                name: 'tools.menuTools.devTools',
                layout: 'tiles',
                list: [
                    { name: 'tools.menuTools.skills', path: '/SkillHub', desc: 'tools.menuTools.skillsDesc', type: 'route' },
                    { name: 'tools.menuTools.onlineLab', path: '/OnlineLab', desc: 'tools.menuTools.onlineLabDesc', type: 'route' },
                    {
                        name: 'tools.menuTools.kernelGen',
                        path: 'https://kernelgen.flagos.io/?lang=zh',
                        enPath: 'https://kernelgen.flagos.io/?lang=en',
                        desc: 'tools.menuTools.kernelGenDesc',
                        type: 'link',
                    },
                    {
                        name: 'tools.menuTools.kernelGenBench',
                        path: 'https://docs.flagos.io/projects/kernelgenbench/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/kernelgenbench/en/latest/',
                        desc: 'tools.menuTools.kernelGenBenchDesc',
                        type: 'link',
                        badge: 'NEW',
                    },
                    {
                        name: 'tools.menuTools.flagOSCompressor',
                        path: 'https://docs.flagos.io/projects/FlagOS-Compressor/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/FlagOS-Compressor/en/latest/',
                        desc: 'tools.menuTools.flagOSCompressorDesc',
                        type: 'link',
                        badge: 'NEW',
                    },
                    {
                        name: 'tools.menuTools.flagPrism',
                        path: 'https://docs.flagos.io/projects/FlagPrism/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/projects/FlagPrism/en/latest/',
                        desc: 'tools.menuTools.flagPrismDesc',
                        type: 'link',
                        badge: 'NEW',
                    },
                ],
            },
            {
                name: 'tools.menuTools.platformService',
                layout: 'list',
                list: [
                    // { name: 'projects.evaluationSystem', path: '/EvaluationSystem', desc: 'projects.evaluationSystemDesc', type: 'route' },
                    {
                        name: 'tools.menuTools.jiudingCloud',
                        path: 'https://platform-multi.baai.ac.cn/client/quantumAI',
                        enPath: 'https://platform-multi.baai.ac.cn/client/quantumAI',
                        desc: 'tools.menuTools.jiudingCloudDesc',
                        type: 'link',
                        badge: 'NEW',
                    },
                    {
                        name: 'tools.menuTools.evaluationSystem',
                        path: '/EvaluationSystem',
                        desc: 'tools.menuTools.evaluationSystemDesc',
                        type: 'route',
                    },
                ],
            },
        ],
    },
    // {
    //     name: 'common.resourcedownload',
    //     path: '/resourcedownload',
    // },
    // 开发者中心（developerCenter）：两栏下拉
    // 左栏「文档与指南」为磁贴（文档中心 + 芯片适配指南），右栏「学习与培训」为卡片列表
    {
        name: 'common.developerCenter',
        dropdownList: [
            {
                name: 'developerCenter.docsAndGuides',
                layout: 'tiles',
                list: [
                    {
                        name: 'developerCenter.docCenter',
                        desc: 'developerCenter.docCenterDesc',
                        path: 'https://docs.flagos.io/zh-cn/latest/',
                        enPath: 'https://docs.flagos.io/en/latest/',
                        type: 'link',
                    },
                    // 芯片适配指南：磁贴内再挂两条适配路径（原型 .menu-tile.guide-tile）
                    {
                        name: 'developerCenter.chipGuide',
                        desc: 'developerCenter.chipGuideDesc',
                        variant: 'guide',
                        list: [
                            {
                                name: 'developerCenter.cloudChip',
                                desc: 'developerCenter.cloudChipDesc',
                                path: 'https://docs.flagos.io/zh-cn/latest/chip_adaptation_guide/cloud_adaptation_guide_index.html',
                                enPath: 'https://docs.flagos.io/en/latest/chip_adaptation_guide/cloud_adaptation_guide_index.html',
                                type: 'link',
                            },
                            {
                                name: 'developerCenter.edgeChip',
                                desc: 'developerCenter.edgeChipDesc',
                                path: 'https://docs.flagos.io/zh-cn/latest/chip_adaptation_guide/edge_adaptation_guide_index.html',
                                enPath: 'https://docs.flagos.io/en/latest/chip_adaptation_guide/edge_adaptation_guide_index.html',
                                type: 'link',
                            },
                        ],
                    },
                ],
            },
            {
                name: 'developerCenter.learnAndTraining',
                layout: 'list',
                list: [
                    {
                        name: 'developerCenter.professionalCourses',
                        desc: 'developerCenter.professionalCoursesDesc',
                        path: '/ProfessionalCourses',
                        type: 'route',
                    },
                    {
                        name: 'developerCenter.techLectures',
                        desc: 'developerCenter.techLecturesDesc',
                        path: '/LearnCenter',
                        type: 'route',
                    },
                    {
                        name: 'developerCenter.globalAITeacherTraining',
                        desc: 'developerCenter.globalAITeacherTrainingDesc',
                        path: '/Icttp',
                        type: 'route',
                    },
                ],
            },
        ],
    },
    {
        name: 'common.metting',
        path: '/Conferences',
    },
    {
        name: 'common.events',
        path: '/events',
    },
    {
        name: 'common.onlineLab',
        path: '/OnlineLab',
    },
    {
        name: 'common.about',
        path: '/About',
        dropdownList: [
            { name: 'menuAbout.AboutFlagOS', desc: 'menuAbout.AboutFlagOSDesc', path: '/About', type: 'route' },
            { name: 'menuAbout.BrandGuidelines', desc: 'menuAbout.BrandGuidelinesDesc', path: '/flagoslogo', type: 'route' },
            { name: 'menuAbout.ContactUs', desc: 'menuAbout.ContactUsDesc', path: '/contact', type: 'route' },
        ],
    },
];
